import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiClientError } from '../api/client';
import { messageFor } from '../api/messages';
import { pairingApi, type AvailableStation } from '../api/stations';
import { useAuth } from '../auth/AuthContext';
import { Alert, Button, Card, EmptyState } from '../components/ui';
import { PosNotice, type PosHandle } from '../pos/usePos';
import { Sheet } from './posParts';

/** Texte zu den Ablehnungen beim Einrichten und Übergeben (sonst die allgemeine Meldung, nie technische Details). */
const SETUP_ERRORS: Record<string, string> = {
  STATION_IN_USE:
    'An diesem Kassenplatz ist schon ein Gerät gekoppelt. Wählen Sie einen anderen Platz oder bitten Sie das Team, das Gerät abzukoppeln.',
  SALES_CLOSED:
    'Im Moment lässt sich keine Kasse einrichten (nur während Annahme, Verkauf und Rückgabe).',
  HANDOVER_PENDING:
    'Es sind noch Verkäufe dieser Kasse nicht übertragen. Bitte zuerst mit Netz übertragen, sonst würden sie der nächsten Person zugerechnet.',
};

function setupMessage(error: unknown): string {
  if (error instanceof PosNotice) return error.message;
  if (error instanceof ApiClientError) {
    const known = SETUP_ERRORS[error.code];
    if (known !== undefined) return known;
  }
  return messageFor(error);
}

/**
 * Einrichtung der Kasse auf diesem Gerät (Kassenmodell E14-2): Kassen gibt es nur noch als eingerichtete Kassenplätze. Die Person
 * wählt einen freien Platz oder koppelt das Gerät per QR-Code. Einen freien Namen gibt es nicht mehr. Belegte Plätze sind
 * sichtbar, aber nicht wählbar (ein Gerät je Platz).
 */
export function StationSetup({ pos, eventId }: { pos: PosHandle; eventId: string }) {
  const [stations, setStations] = useState<AvailableStation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const guard = { cancelled: false };
    pairingApi.availableStations(eventId).then(
      (loaded) => {
        if (!guard.cancelled) setStations(loaded);
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(setupMessage(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [eventId, reload]);

  async function choose(station: AvailableStation) {
    setBusyId(station.id);
    setError(null);
    try {
      await pos.openStation(station.id);
    } catch (caught) {
      setError(setupMessage(caught));
      // Vielleicht ist der Platz inzwischen belegt: Liste neu laden
      setReload((n) => n + 1);
    } finally {
      setBusyId(null);
    }
  }

  const pair = (
    <Link className="btn btn--secondary" to="/kasse-scannen">
      Kasse per QR koppeln
    </Link>
  );
  return (
    <div className="stack stack--lg">
      <Card>
        <div className="stack">
          <p>
            Diese Kasse ist noch nicht eingerichtet. Wählen Sie Ihren Kassenplatz. Dafür braucht das
            Gerät einmal Netz; danach arbeitet die Kasse auch ohne. Am besten richten Sie sie schon
            am Vorabend ein und laden die Artikel.
          </p>
          {error !== null && <Alert kind="error">{error}</Alert>}
          {stations === null ? (
            error === null && <p role="status">Einen Moment bitte …</p>
          ) : stations.length === 0 ? (
            <EmptyState title="Noch kein Kassenplatz eingerichtet" action={pair}>
              Das Team richtet die Kassenplätze des Basars ein. Oder Sie koppeln dieses Gerät mit
              dem QR-Code Ihres Kassenplatzes.
            </EmptyState>
          ) : (
            <ul className="stack" aria-label="Kassenplätze">
              {stations.map((station) => (
                <li key={station.id}>
                  <Button
                    variant={station.occupied ? 'secondary' : 'primary'}
                    disabled={station.occupied || busyId !== null}
                    busy={busyId === station.id}
                    onClick={() => void choose(station)}
                  >
                    {station.occupied ? `${station.label} (belegt)` : station.label}
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {stations !== null && stations.length > 0 && <div className="row">{pair}</div>}
        </div>
      </Card>
    </div>
  );
}

/**
 * Die Kasse wurde an diesem Gerät übergeben: Die Person, die sich jetzt angemeldet hat, übernimmt sie. Die Kasse bleibt dieselbe,
 * frühere Verkäufe bleiben bei der Person, die sie kassiert hat. Die bisherige Person kann die Übergabe zurücknehmen.
 */
export function TakeOver({ pos, label }: { pos: PosHandle; label: string }) {
  const [busy, setBusy] = useState<'take' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function run(kind: 'take' | 'cancel') {
    setBusy(kind);
    setError(null);
    try {
      if (kind === 'take') await pos.takeOver();
      else await pos.cancelHandover();
    } catch (caught) {
      setError(
        kind === 'take' && caught instanceof ApiClientError && caught.status === 404
          ? 'Die Übergabe ist abgelaufen oder wurde zurückgenommen. Die Person, die übergeben hat, kann sie hier zurücknehmen; sonst koppelt das Team das Gerät neu.'
          : setupMessage(caught),
      );
    } finally {
      setBusy(null);
    }
  }
  return (
    <Card>
      <div className="stack">
        <p>
          <strong>{label}</strong> wurde an diesem Gerät übergeben. Mit „Kasse übernehmen“ arbeiten
          Sie mit Ihrem Konto an derselben Kasse weiter. Frühere Verkäufe bleiben bei der Person,
          die sie kassiert hat.
        </p>
        {error !== null && <Alert kind="error">{error}</Alert>}
        <div className="row">
          <Button busy={busy === 'take'} disabled={busy !== null} onClick={() => void run('take')}>
            Kasse übernehmen
          </Button>
          <Button
            variant="secondary"
            busy={busy === 'cancel'}
            disabled={busy !== null}
            onClick={() => void run('cancel')}
          >
            Übergabe zurücknehmen
          </Button>
          <Link className="btn btn--secondary" to="/profil">
            Konto und Abmelden
          </Link>
        </div>
      </div>
    </Card>
  );
}

/**
 * „Kasse übergeben“ im Kassenmenü (Schichtwechsel): erst alles übertragen und den Warenkorb abschließen, dann gibt der Server die
 * Kasse frei (Einmal-Code nur für die Übernahme), das Gerät kassiert nicht mehr und die Person meldet sich ab. Die nächste
 * Person meldet sich an und übernimmt dieselbe Kasse. Den Bargeldabgleich macht man vorher gemeinsam: Danach sieht ihn nur noch
 * die übernehmende Person (und die Leitung).
 */
export function HandoverSheet({
  pos,
  eventId,
  cartEmpty,
  parkedCount,
  offline,
  discardParked,
  onClose,
}: {
  pos: PosHandle;
  eventId: string;
  cartEmpty: boolean;
  parkedCount: number;
  offline: boolean;
  discardParked: () => Promise<void>;
  onClose: () => void;
}) {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = pos.pending.sales + pos.pending.voids + pos.pending.failed;
  const blocked = !cartEmpty || open > 0 || offline;
  async function hand() {
    setBusy(true);
    setError(null);
    try {
      await pos.prepareHandover();
      if (parkedCount > 0) await discardParked();
      await logout();
    } catch (caught) {
      setError(setupMessage(caught));
      setBusy(false);
    }
  }
  return (
    <Sheet title="Kasse übergeben" onClose={onClose}>
      <p>
        Die Kasse bleibt bestehen, nur die Person wechselt. Sie werden abgemeldet; die nächste
        Person meldet sich an diesem Gerät mit ihrem Konto an und übernimmt die Kasse.
      </p>
      <p>
        Tipp: Zählen Sie das Bargeld vorher gemeinsam mit der nächsten Person (
        <Link to={`/basare/${eventId}/bargeld`}>Bargeld zählen</Link>). Nach der Übergabe sieht den
        Bargeldabgleich dieser Kasse nur noch die übernehmende Person und die Leitung.
      </p>
      {!cartEmpty && <Alert kind="info">Bitte erst den Warenkorb abschließen oder leeren.</Alert>}
      {offline && <Alert kind="info">Die Übergabe braucht Netz.</Alert>}
      {open > 0 && (
        <Alert kind="info">
          Es sind noch {open} Vorgänge nicht übertragen. Bitte zuerst mit Netz übertragen.
        </Alert>
      )}
      {parkedCount > 0 && (
        <p className="muted">
          Geparkte Körbe gehen bei der Übergabe verloren (kein Geld betroffen).
        </p>
      )}
      {error !== null && <Alert kind="error">{error}</Alert>}
      <div className="row">
        <Button busy={busy} disabled={blocked} onClick={() => void hand()}>
          Übergeben und abmelden
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * Die Kasse auf diesem Gerät gehört einer anderen Person (Schicht davor, ohne Übergabe abgemeldet). Hier wird weder kassiert
 * noch gesendet: Was noch wartet, geht unter dem Konto der Besitzerin hinaus, sobald sie sich anmeldet. Warten keine Verkäufe
 * mehr, kann die angemeldete Person die Kasse vom Gerät entfernen und ihre eigene einrichten.
 */
export function ForeignRegister({
  pos,
  waiting,
  label,
}: {
  pos: PosHandle;
  waiting: number;
  label: string;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function discard() {
    setBusy(true);
    setError(null);
    try {
      await pos.discardForeign();
    } catch (caught) {
      setError(setupMessage(caught));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <div className="stack">
        <p>
          <strong>{label}</strong> gehört einer anderen Person und ist an diesem Gerät noch auf
          deren Konto eingerichtet.
        </p>
        {waiting > 0 ? (
          <p>
            Es {waiting === 1 ? 'wartet' : 'warten'} noch {waiting} Verkauf
            {waiting === 1 ? '' : 'e'} oder Storno{waiting === 1 ? '' : 's'} auf dieser Kasse. Sie
            gehen unter dem Konto dieser Person hinaus, sobald sie sich hier wieder anmeldet. Nichts
            geht verloren. Bitten Sie diese Person, sich hier kurz anzumelden.
          </p>
        ) : (
          <p>
            Auf dieser Kasse wartet nichts mehr. Sie können sie vom Gerät entfernen und Ihre eigene
            einrichten.
          </p>
        )}
        {error !== null && <Alert kind="error">{error}</Alert>}
        <div className="row">
          {waiting === 0 && (
            <Button busy={busy} disabled={busy} onClick={() => void discard()}>
              Kasse vom Gerät entfernen
            </Button>
          )}
          <Link className="btn btn--secondary" to="/profil">
            Konto und Abmelden
          </Link>
        </div>
      </div>
    </Card>
  );
}
